import type { PlayerEventSettings } from '@/types/preview.types';

const clamp = (value: number, min: number, max: number): number => {
  if (Number.isNaN(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
};

const hexToRgba = (hexColor: string, alpha: number): string => {
  const hex = hexColor.replace('#', '');
  const normalized =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;

  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);

  const a = clamp(alpha, 0, 1);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
};

const getOverlayGradientFromParts = (
  overlayEnabled: boolean,
  overlayColor: string | undefined,
  overlayTransparency: string | number | undefined,
): string | null => {
  if (!overlayEnabled || !overlayColor) return null;

  let transparencyNormalized: number;
  if (typeof overlayTransparency === 'number') {
    transparencyNormalized =
      overlayTransparency > 1 ? clamp(overlayTransparency, 0, 100) / 100 : clamp(overlayTransparency, 0, 1);
  } else if (typeof overlayTransparency === 'string' && overlayTransparency !== '') {
    const raw = parseFloat(overlayTransparency);
    if (Number.isNaN(raw)) {
      transparencyNormalized = 0.5;
    } else {
      transparencyNormalized = raw > 1 ? clamp(raw, 0, 100) / 100 : clamp(raw, 0, 1);
    }
  } else {
    transparencyNormalized = 0.5;
  }
  const alpha = clamp(1 - transparencyNormalized, 0, 1);

  const rgba = overlayColor.startsWith('#') ? hexToRgba(overlayColor, alpha) : `rgba(0, 0, 0, ${alpha})`;

  return `linear-gradient(${rgba}, ${rgba})`;
};

const getOverlayGradient = (settings?: PlayerEventSettings): string | null => {
  if (!settings) return null;
  return getOverlayGradientFromParts(
    Boolean(settings.backgroundOverlay),
    settings.backgroundOverlayColor || '#000000',
    settings.backgroundOverlayTransparency,
  );
};

/** Overlay gradient from baked background layer data (player parity). */
export const getOverlayGradientFromLayerData = (data?: Record<string, string>): string | null => {
  if (!data) return null;
  return getOverlayGradientFromParts(data.overlayEnabled === 'true', data.overlayColor, data.overlayTransparency);
};

/** Styles for a background layer when only baked layer data is available. */
export const getBackgroundStylesFromLayerData = (data?: Record<string, string>) => {
  const imageUrl = data?.imageUrl;
  const backgroundColor = data?.backgroundColor || '#000';
  const overlayGradient = getOverlayGradientFromLayerData(data);

  if (imageUrl) {
    return {
      backgroundImage: overlayGradient ? `${overlayGradient}, url(${imageUrl})` : `url(${imageUrl})`,
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center' as const,
      backgroundRepeat: 'no-repeat' as const,
    };
  }

  if (overlayGradient) {
    return {
      bgcolor: backgroundColor,
      backgroundImage: overlayGradient,
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center' as const,
      backgroundRepeat: 'no-repeat' as const,
    };
  }

  return { bgcolor: backgroundColor };
};

export const getBackgroundStyles = (settings?: PlayerEventSettings) => {
  if (!settings) {
    return { bgcolor: 'background.default' };
  }

  const overlayGradient = getOverlayGradient(settings);

  if (settings.backgroundType === 'color' && settings.backgroundColor) {
    if (overlayGradient) {
      return {
        bgcolor: settings.backgroundColor,
        backgroundImage: overlayGradient,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
    return { bgcolor: settings.backgroundColor };
  }

  if (settings.backgroundType === 'image' && settings.backgroundImageUrl) {
    const backgroundImage = overlayGradient
      ? `${overlayGradient}, url(${settings.backgroundImageUrl})`
      : `url(${settings.backgroundImageUrl})`;

    return {
      backgroundImage,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }

  return { bgcolor: 'background.default' };
};

export const getTextColor = (settings?: PlayerEventSettings) => {
  if (!settings || !settings.textColor) {
    return 'black';
  }
  return settings.textColor;
};
